import React from "react";
import * as XLSX from "xlsx";
import * as CO from "./commons";
import * as echarts from "echarts";
import * as H from "../../../hooks";
import * as C from "../../../Common";
import { LANG, T, TB, TC } from "../../../Constants";

type Data = ReturnType<T.API.Report.Dashboard.GetESGSectionData>["bubble"][number];

export type BubbleProps = {
    /** The data to display */
    data: Data[];
    /** The unit of the data */
    unit: string;
    /** The color palette to use */
    palette: string[];
    /** Is the dashboard energy ? */
    is_energy: boolean;
    /** Is the dashboard co2 ? */
    is_co2: boolean;
    /** The energy type */
    energy?: string;
    /** The y axis title */
    y_axis_title: string;
    /** Is the component refreshing */
    refreshing?: boolean;
    /** Callback when a bubble item is clicked */
    on_bubble_click: (id: string, name: string, energy_type?: string, color?: string, reset_selected_bubble?: boolean, data?: BubbleProps["bubble_info"]) => void;
    /** Callback to refresh the data */
    refresh: (params?: Parameters<T.API.Report.Dashboard.GetESGSectionData>[0]["other"]) => void;
    /** The bubble info */
    bubble_info?: {
        const_year: number;
        value_surface: number;
        diameter: number;
        emplacement: string;
        segment: string;
        enseigne_name: string;
        surface: number;
        emplacement_id: string;
    }
};

export type BubbleRef = {
    /** Is the data segmented ? */
    segmented: boolean;
    /** Display last year's data ? */
    last_year: boolean;
}


const Bubble = React.forwardRef<BubbleRef, BubbleProps>(({ on_bubble_click, refresh, ...props }, ref) => {
    const lg = H.useLanguage();
    const size = H.useWindowSize();
    const segmented = H.useBoolean(true);
    const last_year = H.useBoolean(false);
    const chartRef = React.useRef<HTMLDivElement>(null);
    const [show_data, set_show_data] = React.useState<"all" | "best" | "worst">("all");
    React.useImperativeHandle(ref, () => ({ segmented: segmented.value, last_year: last_year.value }), [segmented.value, last_year.value]);
    const local_lang_value = React.useMemo(() => LANG.getHtmlLanguageCode(lg.prop), [lg.prop]);
    const [export_data, set_export_data] = React.useState<Data[]>(props.data);
    const [bubble_palette, set_bubble_palette] = React.useState<{ color: string; name: string }[]>([]);
    const [padding, set_padding] = React.useState<number>(50);

    const excel_export = React.useCallback(() => {
        if (export_data) {
            const data = export_data.map(d => ({
                emplacement_id: d.emplacement_id,
                [lg.getStaticText(TC.DASH_ENV_CONFIG_GROUPING)]: d.segment,
                [lg.getStaticText(TC.NRJ_DASH_STORE_SIGN)]: d.enseigne_name,
                [lg.getStaticText(TC.BUILD_CONST_DATE)]: d.const_year,
                [lg.getStaticText(TC.GLOBAL_LOCATION)]: d.emplacement,
                [lg.getStaticText(TC.DASH_ENV_VALUE_SURFACE)]: TB.formatNumber(d.value_surface, local_lang_value),
                [lg.getStaticText(TC.GLOBAL_VALUE)]: TB.formatNumber(d.diameter, local_lang_value),
                [lg.getStaticText(TC.GLOBAL_SURFACE)]: TB.formatNumber(d.surface, local_lang_value),
                [lg.getStaticText(TC.DASH_ENV_FILTER_COUNTRY)]: d.country,
                [lg.getStaticText(TC.GLOBAL_AFFECTATION)]: d.actif_type,
                ...(props.is_energy ? {
                    ["Benchmark " + lg.getStaticText(TC.NRJ_DASH_MARKET)]: TB.formatNumber(d.actif_type_benchmark_value, local_lang_value),
                    ["CRREM"]: TB.formatNumber(d.actif_type_crrem_value, local_lang_value),
                } : {}),
                ...(props.is_co2 ? {
                    ["CRREM CO2"]: TB.formatNumber(d.actif_type_crrem_co2_value, local_lang_value),
                } : {}),
            })).sort((a, b) => TB.sortDates(a[lg.getStaticText(TC.BUILD_CONST_DATE)], b[lg.getStaticText(TC.BUILD_CONST_DATE)]));
            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "sheet1");
            XLSX.writeFile(wb, "BubbleChart.xlsx");
        }
    }, [export_data]);

    React.useEffect(() => {
        if (size.width < 1600) {
            set_padding(70);
        } else {
            set_padding(30);
        }
    }, [size]);

    const linearRegression = React.useCallback((data: [number, number][]) => {
        const n = data.length;
        if (n < 2) return [NaN, NaN]; // Vérification si pas assez de points
        let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;

        for (let i = 0; i < n; i++) {
            sumX += data[i][0];
            sumY += data[i][1];
            sumXY += data[i][0] * data[i][1];
            sumXX += data[i][0] * data[i][0];
        }

        const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX); // Calcul de la pente
        const intercept = (sumY - slope * sumX) / n; // L'intercept est la moyenne des Y moins la pente multipliée par la moyenne des X
        return [slope, intercept];
    }, []);

    const chart_data = React.useMemo(() => {

        const segmentedData = props.data.reduce((acc, item) => {
            item.segment = item.segment || lg.getStaticText(TC.NRJ_DASH_ALL);
            if (!acc[item.segment]) acc[item.segment] = [];
            acc[item.segment].push(item);
            return acc;
        }, {} as Record<string, Data[]>);

        // Sort segmentedData segment by sum of value_surface, not the element in the segment
        const sortedSegment = Object.keys(segmentedData).sort((a, b) => {
            const sum_a = segmentedData[a].reduce((acc, item) => acc + item.value_surface, 0);
            const sum_b = segmentedData[b].reduce((acc, item) => acc + item.value_surface, 0);
            return sum_b - sum_a;
        });

        // Sort segmentedData by sortedSegment order
        const sortedSegmentedData = sortedSegment.reduce((acc, segment) => {
            if (segmentedData[segment]) acc[segment] = segmentedData[segment];
            // If not segmented, use the entire dataset
            else if (!segmented.value && segment === 'All') acc[segment] = props.data;
            return acc;
        }, {} as Record<string, Data[]>);

        // remove all data where value_surface is null or const_year is null of the sortedSegmentedData
        Object.keys(sortedSegmentedData).forEach(segment => {
            sortedSegmentedData[segment] = sortedSegmentedData[segment].filter(item => item.value_surface != null && item.value_surface != 0 && item.const_year != null);
        });

        const allData = Object.values(sortedSegmentedData)
            .flat()
            .filter(item => item.value_surface != null && item.value_surface != 0 && item.const_year != null);

        // Calculate the number of items to select
        const numItems = Math.max(1, Math.floor(allData.length * 0.15));

        // Handle the case where there is only one data point
        let worst15PercentData: Data[] = [];
        let best15PercentData: Data[] = [];
        if (allData.length === 1) {
            const singleData = allData[0];
            worst15PercentData = [singleData];
            best15PercentData = [singleData];
        } else {
            // get the 15% worst data
            worst15PercentData = [...allData].sort((a, b) => b.value_surface - a.value_surface).slice(0, numItems);
            // get the 15% best data
            best15PercentData = [...allData].sort((a, b) => a.value_surface - b.value_surface).slice(0, numItems);
        }

        // sort worst15PercentData by segment like sortedSegmentedData
        const worst15PercentDataBySegment = worst15PercentData.reduce((acc, item) => {
            const segment = item.segment || 'All';
            if (!acc[segment]) acc[segment] = [];
            acc[segment].push(item);
            return acc;
        }, {} as Record<string, Data[]>);

        // sort best15PercentData by segment like sortedSegmentedData
        const best15PercentDataBySegment = best15PercentData.reduce((acc, item) => {
            const segment = item.segment || 'All';
            if (!acc[segment]) acc[segment] = [];
            acc[segment].push(item);
            return acc;
        }, {} as Record<string, Data[]>);

        if (show_data === "best") set_export_data(best15PercentData);
        else if (show_data === "worst") set_export_data(worst15PercentData);
        else set_export_data(allData);

        let finalData = sortedSegmentedData;
        if (show_data === "best") finalData = best15PercentDataBySegment;
        else if (show_data === "worst") finalData = worst15PercentDataBySegment;

        const maxSymbolSize = 45;
        const minSymbolSize = 10;
        const diameters = allData.map((item) => item.diameter);
        let maxDiameter = Math.sqrt(Math.max(...diameters));
        let minDiameter = Math.sqrt(Math.min(...diameters));

        if (isNaN(maxDiameter)) maxDiameter = 1;
        if (isNaN(minDiameter)) minDiameter = 0;
        if (minDiameter === maxDiameter) maxDiameter += 1;

        const { minyear, maxyear } = props.data.reduce((acc, item) => {
            acc.minyear = Math.min(acc.minyear, item.const_year);
            acc.maxyear = Math.max(acc.maxyear, item.const_year);
            return acc;
        }, { minyear: Infinity, maxyear: -Infinity });

        let color_palette_index = 0;
        const series: echarts.EChartsOption["series"] = [];

        Object.keys(finalData).forEach((segment, index) => {

            // Calcul de la régression linéaire une seule fois pour le segment
            const [slope, intercept] = linearRegression(finalData[segment].map((item) => [item.const_year, item.value_surface]));

            // Génération des points pour la ligne de régression
            const regressionLine = [
                [minyear - 5, slope * (minyear - 5) + intercept],
                [maxyear + 5, slope * (maxyear + 5) + intercept],
            ];

            // Ajout de la série de dispersion
            series.push({
                type: "scatter",
                name: segment,
                color: show_data === "best" || show_data === "worst" ? bubble_palette.find(bubble => bubble.name === segment)?.color : props.palette[index],
                selectedMode: 'single',
                symbolSize: data => ((Math.sqrt(data[2]) - minDiameter) / (maxDiameter - minDiameter)) * (maxSymbolSize - minSymbolSize) + minSymbolSize,
                data: finalData[segment].map((item) => [
                    item.const_year,
                    item.value_surface,
                    item.diameter,
                    item.emplacement,
                    item.segment,
                    item.enseigne_name,
                    item.surface,
                    item.emplacement_id
                ]),
            });


            if (show_data === "all") {
                set_bubble_palette(prev_bubble_palette => {
                    const exists = prev_bubble_palette.some(bubble => bubble.name === segment);
                    if (!exists) {
                        return [...prev_bubble_palette, {
                            color: props.palette[index],
                            name: segment,
                        }];
                    }
                    return prev_bubble_palette;
                });
            }

            // Ajout de la série pour la ligne de régression
            series.push({
                type: "line",
                color: show_data === "best" || show_data === "worst" ? bubble_palette.find(bubble => bubble.name === segment)?.color : props.palette[color_palette_index % props.palette.length],
                name: lg.getStaticText(TC.NRJ_DASH_REGRESSION) + " " + segment,
                data: regressionLine,
                showSymbol: false,
                tooltip: {
                    formatter: (params) => {
                        return 'test' + params.data[0] + ' ' + params.data[1];
                    },
                    trigger: 'item'
                },
            });

            color_palette_index++;
        });

        if (props.is_energy) {
            // create a list of all actif_type and their benchmark value
            const actifTypeAndBenchmarkValue = props.data.reduce((acc, item) => {
                if (!acc[item.actif_type]) acc[item.actif_type] = item.actif_type_benchmark_value;
                return acc;
            }, {} as Record<string, number>);

            // add a series for the benchmark value
            Object.keys(actifTypeAndBenchmarkValue).forEach((actifType, index) => {

                if (actifType !== lg.getStaticText(TC.GLOBAL_UNKNOWN)) {

                    // calculate the benchmark regression line
                    const benchmarkRegressionLine = [
                        [minyear - 5, actifTypeAndBenchmarkValue[actifType]],
                        [maxyear + 5, actifTypeAndBenchmarkValue[actifType]],
                    ];

                    let name = 'Benchmark ' + lg.getStaticText(TC.NRJ_DASH_MARKET) + ' ' + actifType;
                    let color = (show_data === "best" || show_data === "worst") ? bubble_palette.find(bubble => bubble.name === name)?.color : props.palette[color_palette_index % props.palette.length];

                    if (benchmarkRegressionLine[0][0] !== null && benchmarkRegressionLine[0][1] !== null && benchmarkRegressionLine[1][0] !== null && benchmarkRegressionLine[1][1] !== null) {
                        series.push({
                            type: "line",
                            showSymbol: false,
                            selectedMode: 'single',
                            data: benchmarkRegressionLine,
                            name: name,
                            color: color,
                        });
                    }

                    // add the color of the line to the palette
                    if (show_data === "all") {
                        set_bubble_palette(prev_bubble_palette => {
                            const exists = prev_bubble_palette.some(bubble => bubble.name === name);
                            if (!exists) {
                                return [...prev_bubble_palette, { color: color, name: name }];
                            }
                            return prev_bubble_palette;
                        });
                    }

                    color_palette_index++;
                }
            });

            const actifTypeAndCRREMValue = props.data.reduce((acc: Record<string, Record<string, number>>, item) => {
                if (!acc[item.country]) {
                    acc[item.country] = {};
                }
                if (!acc[item.country][item.actif_type]) {
                    acc[item.country][item.actif_type] = item.actif_type_crrem_value;
                }
                return acc;
            }, {});

            Object.keys(actifTypeAndCRREMValue).forEach((country, index) => {

                Object.keys(actifTypeAndCRREMValue[country]).forEach((actifType, index) => {

                    if (actifType !== lg.getStaticText(TC.GLOBAL_UNKNOWN)) {

                        const benchmarkRegressionLine = [
                            [minyear - 5, actifTypeAndCRREMValue[country][actifType]],
                            [maxyear + 5, actifTypeAndCRREMValue[country][actifType]],
                        ];

                        let name = 'CRREM ' + country + ' ' + actifType;

                        let color = (show_data === "best" || show_data === "worst") ? bubble_palette.find(bubble => bubble.name === name)?.color : props.palette[color_palette_index % props.palette.length];

                        if (benchmarkRegressionLine[0][0] !== null && benchmarkRegressionLine[0][1] !== null && benchmarkRegressionLine[1][0] !== null && benchmarkRegressionLine[1][1] !== null) {
                            series.push({
                                type: "line",
                                showSymbol: false,
                                data: benchmarkRegressionLine,
                                name: name,
                                color: color,
                            });
                        }

                        // add the color of the line to the palette
                        if (show_data === "all") {
                            set_bubble_palette(prev_bubble_palette => {
                                const exists = prev_bubble_palette.some(bubble => bubble.name === name);
                                if (!exists) {
                                    return [...prev_bubble_palette, { color: color, name: name }];
                                }
                                return prev_bubble_palette;
                            });
                        }

                        color_palette_index++;
                    }
                });
            });
        }

        if (props.is_co2) {
            const actifTypeAndCRREMCO2Value = props.data.reduce((acc: Record<string, Record<string, number>>, item) => {
                if (!acc[item.country]) {
                    acc[item.country] = {};
                }
                if (!acc[item.country][item.actif_type]) {
                    acc[item.country][item.actif_type] = item.actif_type_crrem_co2_value;
                }
                return acc;
            }, {});

            Object.keys(actifTypeAndCRREMCO2Value).forEach((country, index) => {
                Object.keys(actifTypeAndCRREMCO2Value[country]).forEach((actifType, index) => {

                    if (actifType !== lg.getStaticText(TC.GLOBAL_UNKNOWN)) {

                        const benchmarkRegressionLine = [
                            [minyear - 5, actifTypeAndCRREMCO2Value[country][actifType]],
                            [maxyear + 5, actifTypeAndCRREMCO2Value[country][actifType]],
                        ];

                        let name = 'CRREM CO2 ' + country + ' ' + actifType;
                        let color = (show_data === "best" || show_data === "worst") ? bubble_palette.find(bubble => bubble.name === name)?.color : props.palette[color_palette_index % props.palette.length];

                        if (benchmarkRegressionLine[0][0] !== null && benchmarkRegressionLine[0][1] !== null && benchmarkRegressionLine[1][0] !== null && benchmarkRegressionLine[1][1] !== null) {
                            series.push({
                                type: "line",
                                showSymbol: false,
                                data: benchmarkRegressionLine,
                                name: name,
                                color: color,
                            });
                        }

                        // add the color of the line to the palette
                        if (show_data === "all") {
                            set_bubble_palette(prev_bubble_palette => {
                                const exists = prev_bubble_palette.some(bubble => bubble.name === name);
                                if (!exists) {
                                    return [...prev_bubble_palette, { color: color, name: name }];
                                }
                                return prev_bubble_palette;
                            });
                        }

                        color_palette_index++;
                    }
                });
            });
        }

        if (props.bubble_info) {
            const bubble_data = [props.bubble_info.const_year, props.bubble_info.value_surface, props.bubble_info.diameter, props.bubble_info.emplacement, props.bubble_info.segment, props.bubble_info.enseigne_name, props.bubble_info.surface, props.bubble_info.emplacement_id]
            series.push({
                type: "scatter",
                symbolSize: data => ((Math.sqrt(data[2]) - minDiameter) / (maxDiameter - minDiameter)) * (maxSymbolSize - minSymbolSize) + minSymbolSize,
                itemStyle: {
                    color: 'none',
                    borderColor: '#333',
                    borderWidth: 2
                },
                data: [bubble_data],
            });
        }

        return { data: finalData, series: series, years: { min: minyear, max: maxyear } };
    }, [props.data, props.palette, props.is_energy, linearRegression, segmented.value, show_data, lg, props.bubble_info]);

    const chart_options = React.useMemo<echarts.EChartsOption>(() => ({
        tooltip: {
            trigger: 'item',
            formatter: params => {
                let content = `<div style="text-align:center;">${params.data[4]}<br> 
                    ${lg.getStaticText(TC.GLOBAL_LOCATION)} : ${params.data[3]} <br> 
                    ${TB.formatNumber(params.data[1], local_lang_value)} ${props.unit}/m² (${TB.formatNumber(params.data[2], local_lang_value)} ${props.unit} / ${TB.formatNumber(params.data[6], local_lang_value)} m²)`;

                params.data[5] ? content += `<br> ${lg.getStaticText(TC.NRJ_DASH_STORE_SIGN)} : ${params.data[5]}` : content += ``;
                content += `</div>`;
                return content;
            }
        },
        xAxis: {
            type: 'value',
            nameLocation: 'middle',
            name: lg.getStaticText(TC.BUILD_CONST_DATE),
            min: chart_data.years.min - 5,
            max: chart_data.years.max + 5,
            nameTextStyle: { padding: 10 },
            axisLabel: { formatter: (value: number) => value.toString() },
        },
        yAxis: {
            min: 0,
            type: 'value',
            position: 'left',
            name: lg.getStaticText(props.y_axis_title),
            // Padding: [top, right, bottom, left]
            nameTextStyle: { padding: [0, 0, 10, padding] }
        },
        legend: {
            bottom: 0,
            type: 'scroll',
            selected: {
                // Set regression lines and CRREM lines to be deselected by default
                ...Object.keys(chart_data.data).reduce((acc, segment) => {
                    // Use a translation or variable for "Regression"
                    const regressionName = `${lg.getStaticText(TC.NRJ_DASH_REGRESSION)} ${segment}`;
                    acc[regressionName] = false;
                    return acc;
                }, {} as Record<string, boolean>),
                // Deselect any series with CRREM in the name
                ...chart_data.series.reduce((acc, serie) => {
                    if (typeof serie.name === 'string' && serie.name.includes('CRREM')) {
                        acc[serie.name] = false;
                    }
                    return acc;
                }, {} as Record<string, boolean>),
            },
        },
        title: {
            top: 0,
            left: 'center',
            text: lg.getStaticText(last_year.value ? TC.ESG_DASH_LAST_YEAR : TC.ESG_DASH_CURRENT_YEAR),
            subtext: lg.getStaticText(segmented.value ? TC.ESG_DASH_WITH_SEGMENTATION : TC.ESG_DASH_WITHOUT_SEGMENTATION),
        },
        toolbox: {
            feature: {
                saveAsImage: { title: lg.getStaticText(TC.NRJ_DASH_SAVE_IMAGE) },
                // dataView: { readOnly: false },
                // myTool1: { show: true, title: lg.getStaticText(TC.GLOBAL_REFRESH), icon: CO.icons.refresh, onclick: () => refresh({ is_segmented: segmented.value, use_last_year: last_year.value }) },
                myTool2: { show: true, title: lg.getStaticText(TC.JS_PORTFOLIO_EXPORT), icon: CO.icons.excel, onclick: excel_export },
            }
        },
        series: chart_data.series
    }), [excel_export, refresh, lg, chart_data, segmented.value, last_year.value, props.y_axis_title, props.unit, padding]);

    React.useEffect(() => {
        if (chartRef.current) {
            // Initialisez le graphique une seule fois
            const chartInstance = echarts.init(chartRef.current);
            chartInstance.setOption(chart_options);
            // Store the callback to access it in the cleaner return function
            let handle_click: (params: echarts.ECElementEvent) => void;

            if (typeof on_bubble_click === "function") {
                // Add event listener for bubble clicks
                handle_click = (params: echarts.ECElementEvent) => {
                    if (params.componentType === 'series' && params.seriesType === 'scatter') {

                        // Correct index for emplacement_id
                        const storeName = params.data[3];
                        let energyType = params.data[4];
                        if (props.energy === "water") {
                            energyType = "WATER";
                        } else if (props.energy === "wastes") {
                            energyType = "WASTE";
                        }
                        const emplacementId = params.data[7];
                        const bubble_color_segment = bubble_palette.find(bubble => bubble.name === params.data[4]);
                        const selected_bubble_info: BubbleProps["bubble_info"] = {
                            const_year: params.data[0],
                            value_surface: params.data[1],
                            diameter: params.data[2],
                            emplacement: params.data[3],
                            segment: params.data[4],
                            enseigne_name: params.data[5],
                            surface: params.data[6],
                            emplacement_id: params.data[7],
                        }
                        // Send the data to the parent component
                        if (segmented.value) on_bubble_click(emplacementId, storeName, "('" + energyType + "')", bubble_color_segment?.color, undefined, selected_bubble_info);

                        else on_bubble_click(emplacementId, storeName, undefined, undefined, undefined, selected_bubble_info);
                    }
                };
                chartInstance.on('click', handle_click);
            }

            // Nettoyage pour éviter les fuites de mémoire
            return () => {
                if (typeof handle_click === "function") chartInstance?.off?.('click', handle_click);
                chartInstance?.dispose?.();
            };
        }
    }, [props.data, chart_options, show_data, segmented.value, on_bubble_click, bubble_palette]);

    const labels = React.useMemo(() => ({
        seg: segmented.value ? TC.NRJ_DASH_SHOW_WITHOUT_SEG : TC.NRJ_DASH_SHOW_SEG,
        year: last_year.value ? TC.NRJ_DASH_SHOW_CURRENT_YEAR : TC.NRJ_DASH_SHOW_LAST_YEAR,
    }), [last_year.value, segmented.value]);

    const years_options = React.useMemo<C.ButtonSelectProps<typeof show_data>["options"]>(() => [
        { label: TC.NRJ_DASH_ALL_DATA, value: "all", variant: "primary", icon: "medal", displayInDropdown: false },
        { label: TC.NRJ_DASH_BEST_15, value: "best", variant: "success", icon: "medal", displayInDropdown: false },
        { label: TC.NRJ_DASH_WORST_15, value: "worst", variant: "danger", icon: "medal", displayInDropdown: false },
    ], []);

    const toggle = React.useMemo(() => ({
        year: () => {
            // Reset the selected bubble
            on_bubble_click(null, null, null, null, true, null);
            refresh({ use_last_year: !last_year.value, is_segmented: segmented.value });
            // Toggle the smoothing flag
            last_year.toggle();
        },
        segmentation: () => {
            // Reset the selected bubble
            on_bubble_click(null, null, null, null, true, null);
            refresh({ use_last_year: last_year.value, is_segmented: !segmented.value });
            // Toggle the smoothing flag
            segmented.toggle();
        }
    }), [refresh, last_year, segmented]);

    return <div className='esg-graph d-flex flex-column w-100 h-100'>
        <C.Spinner loading={props.refreshing} min_load_size="100%">
            <div className='flex-grow-1 w-100'>
                <div ref={chartRef} className='h-100' />
            </div>
            <C.Flex direction="row" justifyContent="between" className="mt-2">
                <C.Button size='sm' icon="calendar" text={labels.year} onClick={toggle.year} />

                <C.ButtonSelect<typeof show_data> default_value="all" value={show_data} onChange={(value) => {
                    on_bubble_click(null, null, null, null, true);
                    set_show_data(value);
                }} size="sm" options={years_options} />

                <C.Button size='sm' icon="chart-pie" text={labels.seg} onClick={toggle.segmentation} />
            </C.Flex>
        </C.Spinner>
    </div>
});

Bubble.displayName = "Bubble";
export default Bubble;